import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Grid,
  IconButton,
  Chip,
  CircularProgress,
} from "@mui/material";
import {
  Close,
  Facebook,
  LinkedIn,
  YouTube,
} from "@mui/icons-material";
import InstagramIcon from "@mui/icons-material/Instagram";

const XIcon = () => (
  <Box
    component="span"
    sx={{
      fontFamily: "'Georgia', serif",
      fontWeight: 900,
      fontSize: "1.3rem",
      lineHeight: 1,
      color: "#fff",
      userSelect: "none",
    }}
  >
    𝕏
  </Box>
);

const platforms = [
  {
    key: "facebook",
    name: "Facebook",
    description: "Connect your Facebook Page",
    icon: <Facebook sx={{ color: "#fff", fontSize: "1.8rem" }} />,
    color: "#1877F2",
  },
  {
    key: "instagram",
    name: "Instagram",
    description: "Connect your Instagram Business account",
    icon: <InstagramIcon sx={{ color: "#fff", fontSize: "1.8rem" }} />,
    color: "#E1306C",
  },
  {
    key: "linkedin",
    name: "LinkedIn",
    description: "Connect your LinkedIn Profile or Page",
    icon: <LinkedIn sx={{ color: "#fff", fontSize: "1.8rem" }} />,
    color: "#0A66C2",
  },
  {
    key: "youtube",
    name: "YouTube",
    description: "Coming soon — YouTube integration is on its way",
    icon: <YouTube sx={{ color: "#fff", fontSize: "1.8rem" }} />,
    color: "#FF0000",
    comingSoon: true,
  },
  {
    key: "x",
    name: "X (Twitter)",
    description: "Connect your X (Twitter) account",
    icon: <XIcon />,
    color: "#000000",
  },
];

const ConnectAccountsDialog = ({ open, onClose, onConnect, connectingPlatform }) => {
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { borderRadius: 3 } }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          pb: 1,
          fontWeight: 700,
        }}
      >
        Connect Social Accounts
        <IconButton onClick={onClose} size="small">
          <Close />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        <Grid container spacing={2} sx={{ pt: 1 }}>

          {platforms.map((p) => {
            const isConnecting = connectingPlatform === p.key;
            const isDisabled = p.comingSoon || isConnecting || (!!connectingPlatform && !isConnecting);

            return (
            <Grid item xs={12} key={p.key}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 2,
                  p: 2,
                  borderRadius: 2,
                  border: p.comingSoon ? "1px dashed #d1d5db" : "1px solid #e2e8f0",
                  bgcolor: p.comingSoon ? "#f9fafb" : "#fff",
                  opacity: p.comingSoon ? 0.7 : 1,
                  "&:hover": !p.comingSoon ? { borderColor: p.color, bgcolor: "#fafafa" } : {},
                  transition: "all 0.2s ease",
                }}
              >
                <Box
                  sx={{
                    width: 44,
                    height: 44,
                    borderRadius: 2,
                    bgcolor: p.comingSoon ? "#9ca3af" : p.color,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                  }}
                >
                  {p.icon}
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Typography fontWeight={700} fontSize="0.95rem" color={p.comingSoon ? "text.secondary" : "text.primary"}>
                      {p.name}
                    </Typography>
                    {p.comingSoon && (
                      <Chip
                        label="Soon"
                        size="small"
                        sx={{
                          bgcolor: "#fef3c7",
                          color: "#92400e",
                          fontWeight: 700,
                          fontSize: "0.65rem",
                          height: 20,
                        }}
                      />
                    )}
                  </Box>
                  <Typography variant="body2" color="text.secondary" fontSize="0.82rem">
                    {isConnecting ? "Connecting…" : p.description}
                  </Typography>
                </Box>
                <Button
                  variant={p.comingSoon ? "outlined" : "contained"}
                  size="small"
                  disabled={isDisabled}
                  startIcon={isConnecting ? <CircularProgress size={14} sx={{ color: "#fff" }} /> : undefined}
                  onClick={!p.comingSoon ? () => onConnect(p.key) : undefined}
                  sx={{
                    bgcolor: p.comingSoon ? "transparent" : p.color,
                    borderColor: p.comingSoon ? "#d1d5db" : p.color,
                    color: p.comingSoon ? "#9ca3af" : "#fff",
                    "&:hover": p.comingSoon ? {} : { bgcolor: p.color, opacity: 0.85 },
                    "&.Mui-disabled": { bgcolor: p.comingSoon ? "transparent" : p.color, color: p.comingSoon ? "#9ca3af" : "#fff", borderColor: p.comingSoon ? "#d1d5db" : p.color, opacity: p.comingSoon ? 1 : 0.75 },
                    borderRadius: 2,
                    fontWeight: 700,
                    px: 2,
                    flexShrink: 0,
                  }}
                >
                  {p.comingSoon ? "Soon" : isConnecting ? "Connecting…" : "Connect"}
                </Button>
              </Box>
            </Grid>
            );
          })}
        </Grid>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} variant="outlined" sx={{ borderRadius: 2 }}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ConnectAccountsDialog;
